import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import {
  getEmail,
  getLastName,
  getFirstName,
  getImpersonateId,
  getPhoneNumber,
  getUsername,
} from 'store/user';
import { getMainAccountId, setMainAccount } from 'store/account';
import { useAppDispatch } from 'store/store';
import MainAccount from 'types/MainAccount';

const getOrCreateMainAccount = async ({
  email,
  phoneNumber,
  firstName,
  lastName,
}: {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string | null;
}) => {
  const getMainAccountResponse = await API.get(
    apiName,
    `${apiBasePath}/mainaccounts/find`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.HOME,
        domain: DOMAINS.ACCOUNT,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        hasEmail: !!email,
        hasPhone: !!phoneNumber,
      },
    },
  );
  if (getMainAccountResponse?.result) {
    return getMainAccountResponse.result;
  } else {
    const createBody = {
      email: email,
      first_name: firstName,
      last_name: lastName,
      ...(phoneNumber != null && phoneNumber !== ''
        ? { phone_number: phoneNumber }
        : {}),
    };
    const createMainAccountResponse = await API.post(
      apiName,
      `${apiBasePath}/mainaccounts`,
      {
        body: {
          ...createBody,
        },
      },
      true,
      undefined,
      {
        tags: {
          section: SECTIONS.HOME,
          domain: DOMAINS.ACCOUNT,
          importance: IMPORTANCE.HIGH,
        },
        context: {
          hasEmail: !!email,
          hasPhone: !!phoneNumber,
          hasName: !!firstName,
        },
      },
    );
    return createMainAccountResponse?.result;
  }
};
export const getMainAccountQuery = (
  id?: string | null,
  email?: string,
  phoneNumber?: string | null,
  firstName?: string,
  lastName?: string,
) => ({
  queryKey: ['mainaccount', id],
  queryFn: async () =>
    (id &&
      (await getOrCreateMainAccount({
        email,
        phoneNumber,
        firstName,
        lastName,
      }))) ||
    null,
  enabled: !!id,
});
const useMainAccount = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const username = useSelector(getUsername);
  const impersonateId = useSelector(getImpersonateId);
  const email = useSelector(getEmail);
  const phoneNumber = useSelector(getPhoneNumber);
  const firstName = useSelector(getFirstName);
  const lastName = useSelector(getLastName);
  const mainAccountId = useSelector(getMainAccountId);
  const id = impersonateId ? impersonateId : username;

  useEffect(() => {
    if (!mainAccountId) {
      queryClient.invalidateQueries({ queryKey: ['mainaccount', id] });
    }
  }, [mainAccountId, queryClient, id]);

  const MainAccountQuery = useQuery<MainAccount>(
    getMainAccountQuery(id, email, phoneNumber, firstName, lastName),
  );

  useEffect(() => {
    if (MainAccountQuery?.data?.id) {
      dispatch(setMainAccount(MainAccountQuery.data));

      queryClient.invalidateQueries({
        queryKey: [apiName, 'subscriptionsList'],
      });
    }
  }, [MainAccountQuery?.data, dispatch, queryClient]);

  return MainAccountQuery;
};

export default useMainAccount;
